import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, KeyRound, Power, Save } from 'lucide-react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { erroDaApi } from '@/lib/erroDaApi';
import FotoEditavel from './FotoEditavel';
import PermissoesDoUsuario from './PermissoesDoUsuario';
import type { GrupoDePermissoes, Usuario } from './tipos';

const campo = 'mt-[2px] h-9 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink';
const titulo = 'text-[12px] font-bold tracking-wide text-ink-soft uppercase';

/**
 * Cadastro e edição em blocos: dados, senha, acesso e situação. Foto, senha e
 * situação gravam na hora; nome, e-mail e acesso esperam o Salvar.
 */
export default function FormularioUsuario({
  usuario,
  grupos,
  souAdministrador,
  aoFechar,
  aoMudar,
}: {
  /** Nulo abre o cadastro de usuário novo. */
  usuario: Usuario | null;
  grupos: GrupoDePermissoes[];
  souAdministrador: boolean;
  aoFechar: () => void;
  /** Algo foi gravado: a lista precisa ser recarregada. */
  aoMudar: () => void;
}) {
  const novo = usuario === null;
  const [nome, setNome] = useState(usuario?.nome ?? '');
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [administrador, setAdministrador] = useState(usuario?.administrador ?? false);
  const [marcadas, setMarcadas] = useState(new Set(usuario?.permissoes ?? []));
  const [atual, setAtual] = useState(usuario);
  const [redefinindo, setRedefinindo] = useState(false);
  const [confirmarSituacao, setConfirmarSituacao] = useState(false);

  const bloqueioEdicao = atual?.impede.editar ?? null;
  const bloqueioAdministrador = novo
    ? souAdministrador
      ? null
      : 'Só um Administrador concede ou retira a condição de Administrador.'
    : administrador === atual?.administrador
      ? atual.impede.administrador
      : null;

  const salvar = useMutation({
    mutationFn: async () => {
      const dados = { nome, email, administrador, permissoes: administrador ? [] : [...marcadas] };

      return novo
        ? (await api.post<{ data: Usuario }>('/usuarios', { ...dados, senha })).data.data
        : (await api.put<{ data: Usuario }>(`/usuarios/${atual!.id}`, dados)).data.data;
    },
    onSuccess: () => {
      aoMudar();
      aoFechar();
    },
  });

  const redefinirSenha = useMutation({
    mutationFn: async () => (await api.post<{ data: Usuario }>(`/usuarios/${atual!.id}/senha`, { senha })).data.data,
    onSuccess: (dados) => {
      setAtual(dados);
      setSenha('');
      setRedefinindo(false);
      aoMudar();
    },
  });

  const mudarSituacao = useMutation({
    mutationFn: async () =>
      (await api.post<{ data: Usuario }>(`/usuarios/${atual!.id}/situacao`, { ativo: !atual!.ativo })).data.data,
    onSuccess: (dados) => {
      setAtual(dados);
      setConfirmarSituacao(false);
      aoMudar();
    },
  });

  const campoSenha = (
    <div className="relative">
      <input
        type={mostrarSenha ? 'text' : 'password'}
        autoComplete="new-password"
        value={senha}
        onChange={(evento) => setSenha(evento.target.value)}
        onKeyDown={(evento) => {
          // Redefinir senha não é o submit do formulário: aqui Enter aciona só a redefinição.
          if (novo || evento.key !== 'Enter') return;
          evento.preventDefault();
          if (senha.length >= 8 && !redefinirSenha.isPending) {
            redefinirSenha.mutate();
          }
        }}
        minLength={novo ? 8 : undefined}
        required={novo}
        className={`${campo} pr-[36px]`}
      />
      <button
        type="button"
        onClick={() => setMostrarSenha(!mostrarSenha)}
        title={mostrarSenha ? 'Esconder' : 'Mostrar'}
        className="absolute top-[7px] right-xs rounded p-[3px] text-muted hover:text-ink"
      >
        {mostrarSenha ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && !salvar.isPending && aoFechar()}>
      <DialogContent className="max-w-[680px] p-lg">
        <DialogTitle>{novo ? 'Novo usuário' : `Editar ${atual!.nome}`}</DialogTitle>
        <DialogDescription className="mt-xs">
          {novo
            ? 'A pessoa entra com a senha provisória e define a dela no primeiro acesso.'
            : (bloqueioEdicao ?? (atual!.ativo ? 'Ativo' : 'Inativo: não consegue entrar no sistema.'))}
        </DialogDescription>

        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            salvar.mutate();
          }}
          className="mt-md space-y-lg"
        >
          <section>
            <h3 className={titulo}>Dados</h3>
            {!novo && (
              <div className="mt-sm">
                <FotoEditavel
                  nome={atual!.nome}
                  fotoUrl={atual!.foto_url}
                  rota={`/usuarios/${atual!.id}/foto`}
                  podeEditar={bloqueioEdicao === null}
                  aoMudar={(dados) => {
                    setAtual(dados as Usuario);
                    aoMudar();
                  }}
                />
              </div>
            )}
            <div className="mt-sm grid gap-sm sm:grid-cols-2">
              <label className="block">
                <span className="block text-[12px] font-bold text-muted">Nome</span>
                <input value={nome} onChange={(evento) => setNome(evento.target.value)} maxLength={120} required disabled={bloqueioEdicao !== null} className={campo} />
              </label>
              <label className="block">
                <span className="block text-[12px] font-bold text-muted">E-mail (é o login)</span>
                <input type="email" value={email} onChange={(evento) => setEmail(evento.target.value)} required disabled={bloqueioEdicao !== null} className={campo} />
              </label>
            </div>
            {novo && <p className="mt-xs text-[12px] text-muted">A foto pode ser enviada depois de cadastrar.</p>}
          </section>

          <section>
            <h3 className={titulo}>Senha</h3>
            {novo ? (
              <label className="mt-sm block max-w-[320px]">
                <span className="block text-[12px] font-bold text-muted">Senha provisória (mínimo 8 caracteres)</span>
                {campoSenha}
              </label>
            ) : redefinindo ? (
              <div className="mt-sm flex flex-wrap items-end gap-sm">
                <label className="block w-[260px]">
                  <span className="block text-[12px] font-bold text-muted">Nova senha provisória</span>
                  {campoSenha}
                </label>
                <button
                  type="button"
                  disabled={redefinirSenha.isPending || senha.length < 8}
                  onClick={() => redefinirSenha.mutate()}
                  className="h-9 rounded-md bg-accent px-md text-[13px] font-bold text-white hover:bg-accent/90 disabled:opacity-50"
                >
                  {redefinirSenha.isPending ? 'Redefinindo…' : 'Redefinir'}
                </button>
                <button type="button" onClick={() => setRedefinindo(false)} className="h-9 px-sm text-[13px] text-muted hover:text-ink">
                  Cancelar
                </button>
                <p className="w-full text-[12px] text-muted">As sessões abertas dele caem e ele define outra senha no próximo acesso.</p>
              </div>
            ) : (
              <div className="mt-sm flex flex-wrap items-center gap-sm text-[13px]">
                <span className="text-muted">{atual!.trocar_senha ? 'Ainda com a senha provisória.' : 'Senha definida pela própria pessoa.'}</span>
                {atual!.impede.senha === null ? (
                  <button
                    type="button"
                    onClick={() => setRedefinindo(true)}
                    className="inline-flex items-center gap-xs rounded-md border border-border px-sm py-[5px] font-bold text-ink hover:bg-surface-subtle"
                  >
                    <KeyRound size={14} />
                    Redefinir senha
                  </button>
                ) : atual!.eu ? (
                  <span className="text-muted">A sua senha você troca em Meu perfil.</span>
                ) : (
                  <span className="text-muted">{atual!.impede.senha}</span>
                )}
              </div>
            )}
            {redefinirSenha.isError && (
              <p className="mt-xs text-[13px] font-bold text-danger">{erroDaApi(redefinirSenha.error, 'Não foi possível redefinir a senha.')}</p>
            )}
          </section>

          <section>
            <h3 className={titulo}>Acesso</h3>
            <div className="mt-sm">
              <PermissoesDoUsuario
                grupos={grupos}
                administrador={administrador}
                marcadas={marcadas}
                bloqueioAdministrador={bloqueioEdicao ?? bloqueioAdministrador}
                aoMudarAdministrador={setAdministrador}
                aoMudarMarcadas={setMarcadas}
              />
            </div>
          </section>

          {!novo && (
            <section>
              <h3 className={titulo}>Situação</h3>
              <div className="mt-sm flex flex-wrap items-center gap-sm text-[13px]">
                {confirmarSituacao ? (
                  <>
                    <span className="text-ink">
                      {atual!.ativo
                        ? `Inativar ${atual!.nome}? Ele não entra mais e as sessões abertas caem. O nome continua no histórico.`
                        : `Reativar ${atual!.nome}?`}
                    </span>
                    <button
                      type="button"
                      disabled={mudarSituacao.isPending}
                      onClick={() => mudarSituacao.mutate()}
                      className={`rounded-md px-md py-[6px] font-bold text-white disabled:opacity-50 ${atual!.ativo ? 'bg-danger hover:bg-danger/90' : 'bg-accent hover:bg-accent/90'}`}
                    >
                      {atual!.ativo ? 'Inativar' : 'Reativar'}
                    </button>
                    <button type="button" onClick={() => setConfirmarSituacao(false)} className="px-sm text-muted hover:text-ink">
                      Cancelar
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    disabled={atual!.impede.inativar !== null}
                    title={atual!.impede.inativar ?? undefined}
                    onClick={() => setConfirmarSituacao(true)}
                    className="inline-flex items-center gap-xs rounded-md border border-border px-sm py-[5px] font-bold text-ink hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Power size={14} />
                    {atual!.ativo ? 'Inativar usuário' : 'Reativar usuário'}
                  </button>
                )}
                {atual!.impede.inativar && <span className="text-muted">{atual!.impede.inativar}</span>}
              </div>
              {mudarSituacao.isError && (
                <p className="mt-xs text-[13px] font-bold text-danger">{erroDaApi(mudarSituacao.error, 'Não foi possível mudar a situação.')}</p>
              )}
            </section>
          )}

          {salvar.isError && <p className="text-[13px] font-bold text-danger">{erroDaApi(salvar.error, 'Não foi possível salvar o usuário.')}</p>}

          <div className="flex justify-end gap-sm border-t border-border pt-md">
            <button
              type="button"
              onClick={aoFechar}
              disabled={salvar.isPending}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Fechar
            </button>
            {bloqueioEdicao === null && (
              <button
                type="submit"
                disabled={salvar.isPending}
                className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90 disabled:opacity-50"
              >
                <Save size={14} aria-hidden="true" />
                {salvar.isPending ? 'Salvando…' : novo ? 'Cadastrar usuário' : 'Salvar'}
              </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
